import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import dayjs from 'dayjs'
import { from } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { ProductSpecificationApiService } from 'src/api/product/specification'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { archiveProductSpecification, createProductSpecification, createProductSpecificationFailure, createProductSpecificationSuccess, loadProductSpecifications, loadProductSpecificationsFailure, loadProductSpecificationsSuccess, unarchiveProductSpecification, updateProductSpecification, updateProductSpecificationFailure, updateProductSpecificationSuccess } from './product-specification.actions'

@Injectable()
export class ProductSpecificationEffects {
  private actions$ = inject(Actions)
  private ProductSpecificationApi = inject(ProductSpecificationApiService)
  private toaster = inject(ToasterService)
  private AuthApi = inject(AuthApiService)


  loadProductSpecifications$ = createEffect(() => this.actions$.pipe(
    ofType(loadProductSpecifications),
    switchMap(() =>
      from(this.ProductSpecificationApi.list({ limit: Number.MAX_SAFE_INTEGER })).pipe(
        map(({ data: productSpecifications }) => loadProductSpecificationsSuccess({ productSpecifications })),
        catchAndLog(this.toaster, 'Unable to fetch productSpecifications', loadProductSpecificationsFailure)))))

  createProductSpecification$ = createEffect(() => this.actions$.pipe(
    ofType(createProductSpecification),
    tap(() => this.toaster.progress('Creating product specification...')),
    switchMap(action =>
      from(this.ProductSpecificationApi.create({...action.productSpecification, user_id: this.AuthApi.currentUser.user_id})).pipe(
        map(({ data: productSpecification }) => createProductSpecificationSuccess({ productSpecification })),
        tap(() => this.toaster.success('Product specification created successfully.')),
        catchAndLog(this.toaster, 'Unable to create product specification', createProductSpecificationFailure)))))

  updateProductSpecification$ = createEffect(() => this.actions$.pipe(
    ofType(updateProductSpecification),
    tap(() => this.toaster.progress('Updating product specification...')),
    switchMap(action =>
      from(this.ProductSpecificationApi.update(action.id, {...action.productSpecification, user_id: this.AuthApi.currentUser.user_id})).pipe(
        map(({ data: productSpecification }) => updateProductSpecificationSuccess({ productSpecification })),
        tap(() => this.toaster.success('Product specification updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update product specification', updateProductSpecificationFailure)))))

  archiveProduct$ = createEffect(() => this.actions$.pipe(
    ofType(archiveProductSpecification),
    tap(() => this.toaster.progress('Archiving product specification...')),
    switchMap(action =>
      from(this.ProductSpecificationApi.update(action.id, { archived: dayjs().unix(), user_id: this.AuthApi.currentUser.user_id })).pipe(
        map(({ data: productSpecification }) => updateProductSpecificationSuccess({ productSpecification })),
        tap(() => this.toaster.success('Product specification archived successfully.')),
        catchAndLog(this.toaster, 'Unable to update product', updateProductSpecificationFailure)))))

  unarchiveProductSpecification$ = createEffect(() => this.actions$.pipe(
    ofType(unarchiveProductSpecification),
    tap(() => this.toaster.progress('Unarchiving product specification...')),
    switchMap(action =>
      from(this.ProductSpecificationApi.update(action.id, { archived: 0, user_id: this.AuthApi.currentUser.user_id })).pipe(
        map(({ data: productSpecification }) => updateProductSpecificationSuccess({ productSpecification })),
        tap(() => this.toaster.success('Product specification unarchived successfully.')),
        catchAndLog(this.toaster, 'Unable to update product', updateProductSpecificationFailure)))))
}
